import { useState, Fragment } from 'react';
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { StatusBadge, StatusType } from './StatusBadge';
import clsx from 'clsx';

interface InvoiceLineItem {
  description: string;
  quantity: number;
  unit_price_cents: number;
  total_cents: number;
}

interface Invoice {
  id: string;
  invoice_number: string | null;
  created_at: string;
  total_cents: number;
  currency: string;
  status: StatusType;
  invoice_pdf_url: string | null;
  hosted_invoice_url: string | null;
  line_items: InvoiceLineItem[];
}

interface InvoiceTableProps {
  invoices: Invoice[];
}

function formatCurrency(amountCents: number, currency: string = 'NZD'): string {
  return (amountCents / 100).toLocaleString('en-NZ', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  });
}

export function InvoiceTable({ invoices }: InvoiceTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    const next = new Set(expandedRows);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedRows(next);
  };

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full text-left text-sm text-slate-400">
        <thead className="text-sm text-slate-500 uppercase bg-slate-800/50 border-b border-slate-700">
          <tr>
            <th scope="col" className="px-6 py-3 font-medium">Date</th>
            <th scope="col" className="px-6 py-3 font-medium">Invoice #</th>
            <th scope="col" className="px-6 py-3 font-medium">Amount (NZD)</th>
            <th scope="col" className="px-6 py-3 font-medium">Status</th>
            <th scope="col" className="px-6 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const isExpanded = expandedRows.has(invoice.id);
            return (
              <Fragment key={invoice.id}>
                <tr
                  className={clsx(
                    'border-b border-slate-700 hover:bg-slate-800/30 transition-colors',
                    isExpanded && 'bg-slate-800/30',
                  )}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    {new Date(invoice.created_at).toLocaleDateString('en-NZ', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-mono text-slate-300">
                    {invoice.invoice_number || invoice.id.slice(0, 8)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-white font-medium">
                    {formatCurrency(invoice.total_cents, invoice.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <StatusBadge status={invoice.status as StatusType} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-3">
                      {invoice.invoice_pdf_url && (
                        <a
                          href={invoice.invoice_pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-cyan-400 transition-colors"
                          title="Download PDF"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </a>
                      )}
                      {invoice.hosted_invoice_url && (
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-400 hover:text-cyan-400 transition-colors"
                          title="View on Stripe"
                        >
                          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        </a>
                      )}
                      {invoice.line_items && invoice.line_items.length > 0 && (
                        <button
                          onClick={() => toggleRow(invoice.id)}
                          className="text-slate-400 hover:text-white transition-colors ml-2"
                        >
                          {isExpanded ? (
                            <ChevronUpIcon className="w-4 h-4" />
                          ) : (
                            <ChevronDownIcon className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && invoice.line_items?.length > 0 && (
                  <tr className="bg-slate-800/20 border-b border-slate-700">
                    <td colSpan={5} className="px-6 py-4">
                      <div className="pl-4 border-l-2 border-slate-700 py-2">
                        <h4 className="text-sm font-semibold text-slate-500 uppercase mb-3">
                          Line Items
                        </h4>
                        <ul className="space-y-2">
                          {invoice.line_items.map((item, idx) => (
                            <li key={idx} className="flex justify-between text-sm">
                              <span className="text-slate-300">{item.description}</span>
                              <span className="text-slate-400">
                                {formatCurrency(item.total_cents)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {invoices.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          No invoices found.
        </div>
      )}
    </div>
  );
}
